import React, { useEffect, useRef, useState } from "react";
import { hasRole } from "../../lib/roles";
import { api } from "../../lib/api";
import { toast } from "sonner";
import Pagination, { TableScroller } from "../../components/Pagination";
import { Plus, X, Pencil, Trash2, Check, CheckSquare, Square, Search } from "lucide-react";
import ImageUploader from "../../components/ImageUploader";
import { useAuth } from "../../lib/auth";
import { CANONICAL_STORES, storeLabel } from "../../lib/constants";

const FIXED_OCCASIONS = ["Daily", "Wedding", "Festival", "Gifting"];
const FIXED_CATEGORIES = ["Necklace", "Mangalsutra", "Rings", "Earrings", "Chains", "Bangles", "Pendant", "Anklets"];
// All carats — 24/22/20/18 use explicit daily rates; others derive proportionally from 24K
const PURITIES = ["24K", "23K", "22K", "21K", "20K", "18K", "14K", "10K", "9K", "Silver"];

const empty = {
  name: "", category: "Necklace", metal: "Gold", purity: "22K", weight_grams: 10,
  price: 0,
  has_making_wastage: true,        // false = fixed-price item (price entered directly)
  making_wastage_percent: 16,      // ONE combined % — making + wastage together
  fixed_price: 0,
  making_charge_percent: 12, wastage_percent: 4, stone_charges: 0, huid: "", barcode: "", size: "",
  gem_type: "Diamond", colour: "", carat_weight: 0, rate_per_carat: 0, certification_charge: 3000, gst_percent: 5,
  hallmark: "BIS Hallmark Certified", description: "", images: [],
  gender: "Women", occasion: "Daily", occasions: [], collection: "", gemstone: "None",
  inventory_category: "",
  segment: "",
  is_bestseller: false, is_featured: false, is_new_arrival: true,
};

// Small labelled field wrapper
function Field({ label, required, hint, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-ink">
        {label} {required && <span className="text-red-600">*</span>}
        {hint && <span className="text-ash font-normal"> — {hint}</span>}
      </span>
      {children}
    </label>
  );
}

const inputCls = "border border-line px-3 py-2 bg-pearl rounded-md text-sm";

// Reusable master-list manager for both segments and occasions
function TaxonomyManager({ title, hint, endpoint, items, reload, testidPrefix, withMetal }) {
  const [name, setName] = useState("");
  const [metal, setMetal] = useState("Gold");
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");

  const add = async () => {
    if (!name.trim()) return;
    try {
      await api.post(`/api/crm/${endpoint}`, withMetal ? { name: name.trim(), metal } : { name: name.trim() });
      setName(""); setMetal("Gold"); reload(); toast.success(`${title} added`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveEdit = async (id) => {
    if (!editName.trim()) return;
    await api.patch(`/api/crm/${endpoint}/${id}`, { name: editName.trim() });
    setEditId(null); reload(); toast.success("Updated");
  };
  const setMetalFor = async (id, m) => { await api.patch(`/api/crm/${endpoint}/${id}`, { metal: m }); reload(); };
  const del = async (id) => { if (!window.confirm(`Delete this ${title.toLowerCase()}?`)) return; await api.delete(`/api/crm/${endpoint}/${id}`); reload(); toast.success("Deleted"); };

  return (
    <div className="bg-white border border-line p-4 mb-6">
      <div className="eyebrow text-[10px] mb-2">{hint}</div>
      <div className="flex gap-2 mb-3">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder={`New ${title.toLowerCase()} name`}
          className="border border-line px-3 py-2 bg-pearl text-sm flex-1" data-testid={`${testidPrefix}-name-input`}
          onKeyDown={(e) => e.key === "Enter" && add()} />
        {withMetal && (
          <select value={metal} onChange={(e) => setMetal(e.target.value)} className="border border-line px-2 py-2 bg-pearl text-sm" data-testid={`${testidPrefix}-metal`}>
            <option value="Gold">Gold</option>
            <option value="Silver">Silver</option>
          </select>
        )}
        <button onClick={add} className="btn-maroon text-sm" data-testid={`${testidPrefix}-add-btn`}><Plus size={14}/> Add</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {items.map((s) => (
          <div key={s.id} className="inline-flex items-center gap-2 border border-line rounded-full px-3 py-1 text-sm">
            {editId === s.id ? (
              <>
                <input value={editName} onChange={(e) => setEditName(e.target.value)} className="border-b border-line bg-transparent text-sm w-24 outline-none" autoFocus
                  onKeyDown={(e) => e.key === "Enter" && saveEdit(s.id)} />
                <button onClick={() => saveEdit(s.id)} className="text-green-700"><Check size={14}/></button>
              </>
            ) : (
              <>
                {s.name}
                {withMetal && (
                  <select value={s.metal || "Gold"} onChange={(e) => setMetalFor(s.id, e.target.value)} className="bg-transparent text-[11px] text-gold-dark outline-none border-l border-line pl-1" data-testid={`${testidPrefix}-metal-${s.id}`}>
                    <option value="Gold">Gold</option>
                    <option value="Silver">Silver</option>
                  </select>
                )}
                <button onClick={() => { setEditId(s.id); setEditName(s.name); }} className="text-ash hover:text-maroon"><Pencil size={12}/></button>
                <button onClick={() => del(s.id)} className="text-ash hover:text-red-600"><Trash2 size={12}/></button>
              </>
            )}
          </div>
        ))}
        {items.length === 0 && <span className="text-xs text-ash">None yet — add a few above.</span>}
      </div>
    </div>
  );
}

export default function ProductsAdmin() {
  const { user } = useAuth();
  // Same roles that can delete a single product can bulk-delete
  const canDelete = hasRole(user, ["super_admin", "major_admin"]);
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set()); // product ids ticked for bulk delete
  const [segments, setSegments] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [jewelleryCategories, setJewelleryCategories] = useState([]);
  const [invCategories, setInvCategories] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(empty);
  const [images, setImages] = useState([]);
  const [primaryIdx, setPrimaryIdx] = useState(null); // main display image
  const [hoverIdx, setHoverIdx] = useState(null);     // image shown on card hover
  const [openingStock, setOpeningStock] = useState({}); // {store: qty} — set on create
  const [addStock, setAddStock] = useState(null); // { product, store, qty } — top-up modal

  const openAddStock = (pid) => {
    const p = items.find((x) => x.id === pid) || seenRef.current[pid];
    if (!p) return;
    setAddStock({ product: p, store: CANONICAL_STORES[0], qty: 1 });
  };
  const submitAddStock = async () => {
    if (!(+addStock.qty > 0)) { toast.error("Enter a quantity greater than 0"); return; }
    try {
      await api.post("/api/crm/stock/add", { product_id: addStock.product.id, store: addStock.store, qty: +addStock.qty });
      toast.success("Stock added");
      setAddStock(null); setSelected(new Set()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add stock"); }
  };

  // The server sends ONE page at a time (with the true total), so every product can be
  // reached — there is no cap on how many products the catalogue can hold. Search
  // (name / category) and the Gold / Silver filter run on the server too.
  const PER_PAGE = 15;
  const [search, setSearch] = useState("");        // what is typed in the box
  const [query, setQuery] = useState("");          // the search actually sent (after a short pause)
  const [metalFilter, setMetalFilter] = useState("");   // "" = all, "Gold", "Silver"
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const filterRef = useRef({ q: "", metal: "", page: 1 });
  filterRef.current = { q: query, metal: metalFilter, page };
  const reqSeq = useRef(0);        // ignore a slow, older response arriving after a newer one
  const seenRef = useRef({});      // every product shown so far, so a ticked row can still be used after you change page
  const load = () => {
    const seq = ++reqSeq.current;
    const { q, metal, page: pageNo } = filterRef.current;
    return api.get("/api/crm/products", {
      params: { page: pageNo, page_size: PER_PAGE, ...(q ? { q } : {}), ...(metal ? { metal } : {}) },
    }).then((r) => {
      if (seq !== reqSeq.current) return;
      const d = r.data;
      let rows;
      if (Array.isArray(d)) {
        // An older backend answers with the whole list at once — page it here instead.
        rows = d.slice((pageNo - 1) * PER_PAGE, pageNo * PER_PAGE);
        setTotal(d.length);
      } else {
        rows = d.items || [];
        setTotal(d.total || 0);
      }
      rows.forEach((x) => { seenRef.current[x.id] = x; });
      setItems(rows);
    });
  };
  const loadSegments = () => api.get("/api/crm/segments").then((r) => setSegments(r.data || [])).catch(() => {});
  const loadOccasions = () => api.get("/api/crm/occasions").then((r) => setOccasions(r.data || [])).catch(() => {});
  const loadJewelleryCategories = () => api.get("/api/crm/jewellery-categories").then((r) => setJewelleryCategories(r.data || [])).catch(() => {});
  useEffect(() => { loadSegments(); loadOccasions(); loadJewelleryCategories(); api.get("/api/crm/inventory/categories").then((r) => setInvCategories(r.data || [])).catch(() => {}); }, []);

  const allOccasions = [
    ...FIXED_OCCASIONS,
    ...occasions.map((o) => o.name).filter((n) => !FIXED_OCCASIONS.some((f) => f.toLowerCase() === n.toLowerCase())),
  ];

  // Fixed categories + any admin-added ones (shown here and in the storefront's Jewellery menu)
  const allCategories = [
    ...FIXED_CATEGORIES,
    ...jewelleryCategories.map((c) => c.name).filter((n) => !FIXED_CATEGORIES.some((f) => f.toLowerCase() === n.toLowerCase())),
  ];

  const openCreate = () => { setEditingId(null); setForm(empty); setImages([]); setPrimaryIdx(null); setHoverIdx(null); setOpeningStock({}); setShowForm(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      ...empty,
      ...p,
      has_making_wastage: p.has_making_wastage !== false,
      // Older records had two separate fields — pre-fill the combined % with their sum
      making_wastage_percent: p.making_wastage_percent ?? ((p.making_charge_percent || 0) + (p.wastage_percent || 0) || 16),
      fixed_price: p.fixed_price ?? p.price ?? 0,
    });
    setImages(p.images || []);
    // Convention: images[0] = main, images[1] = hover
    setPrimaryIdx((p.images || []).length > 0 ? 0 : null);
    setHoverIdx((p.images || []).length > 1 ? 1 : null);
    setShowForm(true);
  };

  // Keep marks valid when an image is removed
  const handleImagesChange = (urls) => {
    if (urls.length < images.length) { setPrimaryIdx(null); setHoverIdx(null); }
    setImages(urls);
  };

  const submit = async (e) => {
    e.preventDefault();
    const gem = form.metal === "Gemstone";
    const cap = gem ? 5 : 7;
    if (images.length < 2) { toast.error("Please upload at least 2 images (main + hover)"); return; }
    if (images.length > cap) { toast.error(`Up to ${cap} images allowed`); return; }
    if (primaryIdx == null || hoverIdx == null) { toast.error("Please mark one image as Main and one as Hover"); return; }
    if (primaryIdx === hoverIdx) { toast.error("Main and Hover must be different images"); return; }
    if (gem) {
      if (!(+form.carat_weight > 0) || !(+form.rate_per_carat > 0)) { toast.error("Enter the carat weight and rate per carat"); return; }
    } else if (!form.has_making_wastage && !(+form.fixed_price > 0)) {
      toast.error("This item has no making + wastage — enter its price directly"); return;
    }
    // Reorder so the storefront convention holds: [0] = main, [1] = hover, rest follow
    const rest = images.filter((_, i) => i !== primaryIdx && i !== hoverIdx);
    const ordered = [images[primaryIdx], images[hoverIdx], ...rest];
    // The primary occasion is always part of the display list
    const occs = [...new Set([form.occasion, ...(form.occasions || [])])].filter(Boolean);
    let payload = { ...form, occasions: occs, images: ordered, is_active: true };
    if (gem) {
      // Gemstones price = (carat × rate + certification) + GST%. Stored as a
      // fixed price so the rest of the app needs no special-casing.
      const carat = +form.carat_weight || 0, rate = +form.rate_per_carat || 0;
      const cert = +form.certification_charge || 0, gstP = +form.gst_percent || 0;
      const total = (carat * rate + cert) * (1 + gstP / 100);
      payload = {
        ...payload,
        category: form.gem_type || form.category,
        has_making_wastage: false,
        certification_charge: cert,
        gst_percent: gstP,
        fixed_price: Math.round(total),
      };
    }
    try {
      if (editingId) { await api.patch(`/api/crm/products/${editingId}`, payload); toast.success("Product updated"); }
      else {
        const { data } = await api.post("/api/crm/products", payload);
        const newId = data?.id;
        if (newId) {
          await Promise.all(
            Object.entries(openingStock)
              .filter(([, qty]) => +qty > 0)
              .map(([store, qty]) => api.post("/api/crm/stock/set", { product_id: newId, store, qty: +qty }).catch(() => {}))
          );
        }
        toast.success("Product added");
      }
      setShowForm(false); setEditingId(null); setForm(empty); setImages([]); setPrimaryIdx(null); setHoverIdx(null); setOpeningStock({}); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product permanently? This cannot be undone.")) return;
    await api.delete(`/api/crm/products/${id}`);
    toast.success("Product deleted");
    setSelected((s) => { const n = new Set(s); n.delete(id); return n; });
    load();
  };

  // ---- Bulk selection ----
  const toggleSelect = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const pg = { page, setPage, totalPages, total, start: (page - 1) * PER_PAGE, perPage: PER_PAGE, pageItems: items };

  // Fetch whenever the search, the metal filter or the page changes (this is also
  // the first load when the screen opens).
  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [query, metalFilter, page]);

  // Typing is applied after a short pause. A new search starts again from page 1 and
  // clears any ticked rows: they may no longer be on screen, and "Delete Selected"
  // must never act on rows you cannot see.
  useEffect(() => {
    if (search.trim() === query) return undefined;
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); setSelected(new Set()); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  // If the last page empties (e.g. you deleted its only row), step back to a real page.
  useEffect(() => {
    if (total > 0 && page > totalPages) setPage(totalPages);
  }, [total, page, totalPages]);

  const pickMetal = (val) => { setMetalFilter(val); setPage(1); setSelected(new Set()); };
  const clearSearch = () => { setSearch(""); setQuery(""); setPage(1); setSelected(new Set()); };
  const clearFilters = () => { setSearch(""); setQuery(""); setMetalFilter(""); setPage(1); setSelected(new Set()); };
  const isFiltered = !!(search.trim() || metalFilter);
  // Select-all applies to the PAGE you can see, not the whole catalogue —
  // ticking a header box should never quietly select rows on other pages.
  const allSelected = pg.pageItems.length > 0 && pg.pageItems.every((p) => selected.has(p.id));

  const toggleSelectAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allSelected) pg.pageItems.forEach((p) => next.delete(p.id));
    else pg.pageItems.forEach((p) => next.add(p.id));
    return next;
  });

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} selected product(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/api/crm/products/bulk-delete", { ids: [...selected] });
      toast.success(`${data.deleted ?? selected.size} product(s) deleted`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
  };

  const isGem = form.metal === "Gemstone";
  const maxImages = isGem ? 5 : 7;

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div><div className="eyebrow">Catalogue</div><h1 className="font-display text-4xl text-maroon">Products & Stock</h1></div>
        <div className="flex items-center gap-2">
          {selected.size === 1 && (
            <button onClick={() => openAddStock([...selected][0])} className="inline-flex items-center gap-2 bg-green-700 text-white rounded px-4 py-2 text-sm hover:bg-green-800 transition-colors" data-testid="add-stock-btn">
              <Plus size={14}/> Add Stock
            </button>
          )}
          {canDelete && selected.size > 0 && (
            <button onClick={bulkDelete} className="inline-flex items-center gap-2 bg-red-600 text-white rounded px-4 py-2 text-sm hover:bg-red-700 transition-colors" data-testid="bulk-delete-btn">
              <Trash2 size={14}/> Delete Selected ({selected.size})
            </button>
          )}
          <button onClick={openCreate} className="btn-maroon" data-testid="add-product-btn"><Plus size={14}/> Add Product</button>
          <button onClick={() => setShowCategoryManager(true)} className="inline-flex items-center gap-2 border border-maroon text-maroon rounded px-4 py-2 text-sm hover:bg-maroon hover:text-white transition-colors" data-testid="add-category-btn"><Plus size={14}/> Add Category</button>
        </div>
      </div>

      {showCategoryManager && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowCategoryManager(false)}>
          <div className="bg-white w-full max-w-lg royal-frame" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-line">
              <div className="font-display text-2xl text-maroon">Jewellery Categories</div>
              <button type="button" onClick={() => setShowCategoryManager(false)}><X size={18}/></button>
            </div>
            <div className="p-6">
              <TaxonomyManager
                title="Category" hint="Categories shown under Jewellery in the website header (e.g. Necklace, Rings, Bangles…)"
                endpoint="jewellery-categories" items={jewelleryCategories} reload={loadJewelleryCategories} testidPrefix="jewellery-category" withMetal
              />
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <TaxonomyManager
          title="Segment" hint="Wedding Segments (Haldi, Reception, Engagement…)"
          endpoint="segments" items={segments} reload={loadSegments} testidPrefix="segment"
        />
        <TaxonomyManager
          title="Occasion" hint="Occasions (Anniversary, Birthday, Reception, Name Ceremony…)"
          endpoint="occasions" items={occasions} reload={loadOccasions} testidPrefix="occasion"
        />
      </div>

      {/* Search by name / category + Gold / Silver filter */}
      <div className="flex flex-wrap items-center gap-3 mb-3" data-testid="products-filter-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ash"/>
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, category or barcode…"
            className="w-full border border-line bg-white pl-9 pr-9 py-2.5 text-sm"
            data-testid="product-search"
          />
          {search && (
            <button type="button" onClick={clearSearch} aria-label="Clear search"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-ash hover:text-maroon" data-testid="product-search-clear">
              <X size={14}/>
            </button>
          )}
        </div>
        <div className="inline-flex border border-line bg-white" role="group" aria-label="Filter by metal" data-testid="metal-filter">
          {[["", "All"], ["Gold", "Gold"], ["Silver", "Silver"]].map(([val, label]) => (
            <button key={label} type="button" onClick={() => pickMetal(val)} aria-pressed={metalFilter === val}
              data-testid={`metal-filter-${label.toLowerCase()}`}
              className={`px-4 py-2.5 text-sm transition-colors ${metalFilter === val ? "bg-maroon text-pearl" : "text-ink hover:bg-pearl"}`}>
              {label}
            </button>
          ))}
        </div>
        {isFiltered && (
          <button type="button" onClick={clearFilters}
            className="text-xs text-maroon underline" data-testid="product-filters-clear">Clear filters</button>
        )}
      </div>

      <div className="bg-white border border-line">
        {/* Page controls sit ABOVE the table as well, so there's no need to
            scroll to the bottom just to change page. */}
        <Pagination {...pg} label="products" compact/>
        <TableScroller>
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>
              {canDelete && (
                <th className="p-3 w-8">
                  <button type="button" onClick={toggleSelectAll} className="text-maroon flex" title={allSelected ? "Unselect all" : "Select all"} data-testid="select-all-products">
                    {allSelected ? <CheckSquare size={16}/> : <Square size={16} className="text-ash"/>}
                  </button>
                </th>
              )}
              {["", "Name", "Category", "Occasion", "Segment", "Purity", "Weight", "Size", "Price", "HUID", "Barcode", "Last Updated", "Last Updated Stock", "Actions"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {pg.pageItems.map((p) => (
              <tr key={p.id} className={`border-b border-line/60 ${selected.has(p.id) ? "bg-gold/10" : ""}`} data-testid={`product-row-${p.id}`}>
                {canDelete && (
                  <td className="p-3">
                    <input
                      type="checkbox"
                      className="accent-maroon w-4 h-4 cursor-pointer"
                      checked={selected.has(p.id)}
                      onChange={() => toggleSelect(p.id)}
                      data-testid={`select-product-${p.id}`}
                    />
                  </td>
                )}
                <td className="p-2"><img src={p.images?.[0]} alt="" className="w-10 h-12 object-cover bg-pearl"/></td>
                <td className="p-3">{p.name}</td>
                <td className="p-3 text-ash">{p.category}</td>
                <td className="p-3 text-ash">{p.occasion || "—"}</td>
                <td className="p-3 text-ash">{p.segment || "—"}</td>
                <td className="p-3">{(p.metal === "Gemstone" || p.metal === "Silver" || !p.purity) ? "—" : p.purity}</td>
                <td className="p-3">{p.metal === "Gemstone" ? `${p.carat_weight || 0} ct` : `${p.weight_grams}g`}</td>
                <td className="p-3 text-xs">{p.size || (p.metal === "Gemstone" && p.colour ? p.colour : "—")}</td>
                <td className="p-3">
                  {p.has_making_wastage === false ? (
                    <div>
                      <div>₹{Number(p.fixed_price || p.price || 0).toLocaleString("en-IN")}</div>
                      <div className="text-[9px] text-ash leading-tight" data-testid={`price-updated-${p.id}`}>
                        Last price updated on<br/>
                        {p.price_updated_at ? new Date(p.price_updated_at).toLocaleDateString("en-GB") : "—"}
                        {p.price_updated_at && <><br/>{new Date(p.price_updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</>}
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="text-xs text-ash">Calculated</div>
                      <div className="text-[9px] text-ash">MW {p.making_wastage_percent ?? ((p.making_charge_percent || 0) + (p.wastage_percent || 0))}% + GST</div>
                    </div>
                  )}
                </td>
                <td className="p-3 text-xs">{p.huid || "—"}</td>
                <td className="p-3 text-xs font-mono" data-testid={`barcode-${p.id}`}>{p.barcode || "—"}</td>
                <td className="p-3 text-xs" data-testid={`last-updated-${p.id}`}>
                  {p.last_updated ? (
                    <div>
                      <div>{new Date(p.last_updated).toLocaleDateString("en-GB")}</div>
                      <div className="text-[9px] text-ash">
                        {new Date(p.last_updated).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {p.last_updated_by ? " · " : ""}
                        {p.last_updated_by === "Bulk Upload"
                          ? <span className="bg-gold/30 text-maroon px-1 uppercase tracking-wide">Bulk Upload</span>
                          : (p.last_updated_by || "")}
                        {p.last_updated_role ? <span className="text-ash"> ({(p.last_updated_role || "").replace("_", " ")})</span> : null}
                      </div>
                    </div>
                  ) : "—"}
                </td>
                <td className="p-3 text-xs" data-testid={`stock-updated-${p.id}`}>
                  {p.stock_last_updated_at ? (
                    <div>
                      <div>{new Date(p.stock_last_updated_at).toLocaleDateString("en-GB")}</div>
                      <div className="text-[9px] text-ash">
                        {new Date(p.stock_last_updated_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        {p.stock_last_updated_by ? ` · ${p.stock_last_updated_by}` : ""}
                        {p.stock_last_updated_role ? <span className="text-ash"> ({(p.stock_last_updated_role || "").replace("_", " ")})</span> : null}
                      </div>
                    </div>
                  ) : "—"}
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <button onClick={() => openEdit(p)} className="inline-flex items-center gap-1 text-xs text-maroon hover:underline" data-testid={`edit-product-${p.id}`}>
                      <Pencil size={13}/> Edit
                    </button>
                    <button onClick={() => remove(p.id)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid={`delete-product-${p.id}`}>
                      <Trash2 size={13}/> Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </TableScroller>
        {total === 0 && (
          <div className="p-10 text-center text-sm text-ash" data-testid="products-empty">
            {isFiltered ? "No products match your search or filter." : "No products yet."}
          </div>
        )}
        <Pagination {...pg} label="products"/>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="bg-white border border-line rounded-xl w-full max-w-4xl my-6 max-h-[92vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-line shrink-0">
              <div className="font-display text-2xl text-maroon">{editingId ? "Edit product" : "Add product"}</div>
              <button type="button" onClick={() => setShowForm(false)}><X size={18}/></button>
            </div>

            <div className="p-6 overflow-y-auto space-y-6">

              {/* Basics */}
              <div>
                <div className="eyebrow text-[10px] mb-3">Basic Details</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Product Name" required>
                    <input data-testid="prod-name" required placeholder="e.g. Antique Temple Necklace" className={inputCls} value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/>
                  </Field>
                  <Field label={isGem ? "Gemstone Type" : "Category"} required>
                    {isGem ? (
                      <select data-testid="prod-gem-type" className={inputCls} value={form.gem_type} onChange={(e) => setForm({...form, gem_type: e.target.value, category: e.target.value})}>
                        {["Diamond", "Precious Stone", "Semi-precious Stone"].map((x) => <option key={x}>{x}</option>)}
                      </select>
                    ) : (
                      <select data-testid="prod-category" className={inputCls} value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}>
                        {allCategories.map((x) => <option key={x}>{x}</option>)}
                      </select>
                    )}
                  </Field>
                  <Field label="Live inventory category" hint="Links this product to a live-inventory design so slips reserve the right pieces">
                    <select data-testid="prod-inventory-category" className={inputCls} value={form.inventory_category || ""} onChange={(e) => setForm({...form, inventory_category: e.target.value})}>
                      <option value="">— none (use name/category match) —</option>
                      {invCategories.map((c) => <option key={c.id} value={c.name}>{c.name}{c.parent_category ? ` (${c.parent_category})` : ""}</option>)}
                    </select>
                  </Field>
                  <Field label="Metal / Material" required>
                    <select data-testid="prod-metal" className={inputCls} value={form.metal}
                      onChange={(e) => {
                        const m = e.target.value;
                        setForm((f) => ({ ...f, metal: m, ...(m === "Gemstone" ? { category: f.gem_type || "Diamond", gem_type: f.gem_type || "Diamond" } : {}) }));
                      }}>
                      <option>Gold</option><option>Silver</option><option>Gemstone</option>
                    </select>
                  </Field>
                  {!isGem && (
                    <Field label="Purity" required>
                      <select data-testid="prod-purity" className={inputCls} value={form.purity} onChange={(e) => setForm({...form, purity: e.target.value})}>
                        {PURITIES.map((x) => <option key={x}>{x}</option>)}
                      </select>
                    </Field>
                  )}
                  <Field label="Weight" required={!isGem} hint={isGem ? "grams, optional" : "grams"}>
                    <input data-testid="prod-weight" type="number" step="0.01" required={!isGem} placeholder="e.g. 12.50" className={inputCls} value={form.weight_grams} onChange={(e) => setForm({...form, weight_grams: +e.target.value})}/>
                  </Field>

                  {/* Gemstone-specific fields */}
                  {isGem && (
                    <>
                      <Field label="Colour" hint="e.g. Colourless, Blue, Red">
                        <input data-testid="prod-colour" placeholder="e.g. D / Colourless" className={inputCls} value={form.colour} onChange={(e) => setForm({...form, colour: e.target.value})}/>
                      </Field>
                      <Field label="Weight (carat)" required hint="carats — e.g. 1.25">
                        <input data-testid="prod-carat" type="number" step="0.001" placeholder="e.g. 1.25" className={inputCls} value={form.carat_weight} onChange={(e) => setForm({...form, carat_weight: +e.target.value})}/>
                      </Field>
                      <Field label="Rate per carat (₹)" required hint="price for one carat">
                        <input data-testid="prod-rate-carat" type="number" step="1" placeholder="e.g. 80000" className={inputCls} value={form.rate_per_carat} onChange={(e) => setForm({...form, rate_per_carat: +e.target.value})}/>
                      </Field>
                      <Field label="Certification charges (₹)" hint="default 3000, editable">
                        <input data-testid="prod-cert" type="number" step="1" placeholder="3000" className={inputCls} value={form.certification_charge} onChange={(e) => setForm({...form, certification_charge: +e.target.value})}/>
                      </Field>
                      <Field label="GST (%)" hint="default 5%, editable per category">
                        <input data-testid="prod-gst" type="number" step="0.1" placeholder="5" className={inputCls} value={form.gst_percent} onChange={(e) => setForm({...form, gst_percent: +e.target.value})}/>
                      </Field>
                    </>
                  )}

                  {!isGem && (
                    <Field label="HUID" hint="6-digit hallmark ID, optional">
                      <input data-testid="prod-huid" placeholder="e.g. AB1234" className={inputCls} value={form.huid} onChange={(e) => setForm({...form, huid: e.target.value})}/>
                    </Field>
                  )}
                  {!isGem && (
                    <Field label="Size" hint="ring/bangle size, chain length — optional">
                      <input data-testid="prod-size" placeholder="e.g. 16 / 2.4 / 18 in" className={inputCls} value={form.size} onChange={(e) => setForm({...form, size: e.target.value})}/>
                    </Field>
                  )}
                  <Field label="Barcode" hint="tag number — up to 10 letters/numbers">
                    <input
                      data-testid="prod-barcode"
                      placeholder="e.g. AB12CD34"
                      maxLength={10}
                      className={`${inputCls} uppercase`}
                      value={form.barcode}
                      onChange={(e) => setForm({ ...form, barcode: e.target.value.replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 10) })}
                    />
                  </Field>
                </div>
              </div>

              {/* Gemstone pricing preview */}
              {isGem && (
                <div>
                  <div className="eyebrow text-[10px] mb-3">Pricing</div>
                  <div className="bg-pearl border border-line p-4 text-sm space-y-1" data-testid="gem-price-preview">
                    {(() => {
                      const carat = +form.carat_weight || 0, rate = +form.rate_per_carat || 0;
                      const cert = +form.certification_charge || 0, gstP = +form.gst_percent || 0;
                      const subtotal = carat * rate + cert;
                      const gst = subtotal * gstP / 100;
                      const total = subtotal + gst;
                      return (
                        <>
                          <div className="flex justify-between"><span className="text-ash">Carat × Rate</span><span>{carat} ct × ₹{rate.toLocaleString("en-IN")} = ₹{(carat * rate).toLocaleString("en-IN")}</span></div>
                          <div className="flex justify-between"><span className="text-ash">Certification charges</span><span>₹{cert.toLocaleString("en-IN")}</span></div>
                          <div className="flex justify-between"><span className="text-ash">GST ({gstP}%)</span><span>₹{Math.round(gst).toLocaleString("en-IN")}</span></div>
                          <div className="flex justify-between font-medium text-maroon pt-1 border-t border-line"><span>Total price</span><span>₹{Math.round(total).toLocaleString("en-IN")}</span></div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              {/* Pricing (gold / silver) */}
              {!isGem && (
              <div>
                <div className="eyebrow text-[10px] mb-3">Pricing</div>

                {/* Does this item carry making + wastage charges? */}
                <div className="mb-4">
                  <div className="text-xs font-medium text-ink mb-2">Does this item have Making + Wastage charges?</div>
                  <div className="flex border border-line overflow-hidden w-fit">
                    <button type="button" onClick={() => setForm({...form, has_making_wastage: true})}
                      className={`px-4 py-2 text-xs ${form.has_making_wastage ? "bg-maroon text-pearl" : "bg-white"}`}
                      data-testid="prod-mw-yes">
                      Yes — calculate from rate
                    </button>
                    <button type="button" onClick={() => setForm({...form, has_making_wastage: false})}
                      className={`px-4 py-2 text-xs border-l border-line ${!form.has_making_wastage ? "bg-maroon text-pearl" : "bg-white"}`}
                      data-testid="prod-mw-no">
                      No — I'll enter the price directly
                    </button>
                  </div>
                </div>

                {form.has_making_wastage ? (
                  <>
                    <div className="grid md:grid-cols-2 gap-4">
                      <Field label="Making + Wastage %" required hint="ONE combined percentage — e.g. 16 means metal value + 16%">
                        <input data-testid="prod-making-wastage" type="number" step="0.1" placeholder="e.g. 16" className={inputCls} value={form.making_wastage_percent} onChange={(e) => setForm({...form, making_wastage_percent: +e.target.value})}/>
                      </Field>
                      <Field label="Stone Charges" hint="flat ₹, optional">
                        <input data-testid="prod-stones" type="number" placeholder="e.g. 2000" className={inputCls} value={form.stone_charges} onChange={(e) => setForm({...form, stone_charges: +e.target.value})}/>
                      </Field>
                    </div>
                    <p className="text-[11px] text-ash mt-2" data-testid="pricing-preview-note">
                      Selling price = today's {form.metal === "Silver" ? "silver" : `${form.purity} gold`} rate × {form.weight_grams || 0}g,
                      + {form.making_wastage_percent || 0}% making + wastage{form.stone_charges > 0 ? `, + ${form.stone_charges} stones` : ""}, then 3% GST on that value.
                    </p>
                  </>
                ) : (
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Price (₹)" required hint="entered price is displayed exactly as-is — no making/wastage, no rate calculation">
                      <input data-testid="prod-fixed-price" type="number" step="1" placeholder="e.g. 45000" className={inputCls} value={form.fixed_price} onChange={(e) => setForm({...form, fixed_price: +e.target.value})}/>
                    </Field>
                    <div className="text-[11px] text-ash self-end pb-3">
                      Fixed-price items show a "Last price updated on" stamp in the products list every time this price changes.
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Opening stock per store — only when adding a new product */}
              {!editingId && (
                <div>
                  <div className="eyebrow text-[10px] mb-3">Opening Stock <span className="text-ash normal-case tracking-normal">(optional — how many are in each store now)</span></div>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {CANONICAL_STORES.map((s) => (
                      <Field key={s} label={storeLabel(s)} hint="quantity in this store">
                        <input
                          type="number" min={0} placeholder="0" className={inputCls}
                          value={openingStock[s] ?? ""}
                          onChange={(e) => setOpeningStock({ ...openingStock, [s]: e.target.value })}
                          data-testid={`opening-stock-${s}`}
                        />
                      </Field>
                    ))}
                  </div>
                  <p className="text-[11px] text-ash mt-2">
                    You can also set or change stock any time under <b>Stock by Store</b>. Transfers move quantity from one store to another, so a piece is only ever counted in the store that holds it.
                  </p>
                </div>
              )}

              {/* Classification */}
              <div>
                <div className="eyebrow text-[10px] mb-3">Classification</div>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Gender" required>
                    <select data-testid="prod-gender" className={inputCls} value={form.gender} onChange={(e) => setForm({...form, gender: e.target.value})}>
                      {["Women", "Men", "Kids", "Unisex"].map((x) => <option key={x}>{x}</option>)}
                    </select>
                  </Field>
                  <Field label="Primary Occasion" required>
                    <select data-testid="prod-occasion" className={inputCls} value={form.occasion} onChange={(e) => setForm({...form, occasion: e.target.value})}>
                      {allOccasions.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </Field>
                  <div className="md:col-span-2">
                    <Field label="Display in Occasions" hint="tick every occasion this product should appear under in the storefront filters (e.g. a Mangalsutra should not appear under Birthday)">
                      <div className="flex flex-wrap gap-2 mt-1" data-testid="prod-occasions-multi">
                        {allOccasions.map((x) => {
                          const on = (form.occasions || []).includes(x);
                          return (
                            <button
                              key={x}
                              type="button"
                              onClick={() => {
                                const cur = new Set(form.occasions || []);
                                on ? cur.delete(x) : cur.add(x);
                                setForm({ ...form, occasions: [...cur] });
                              }}
                              className={`text-xs rounded-full border px-3 py-1.5 transition-all ${on ? "bg-maroon text-pearl border-maroon" : "border-line hover:border-maroon"}`}
                              data-testid={`prod-occasions-${x}`}
                            >
                              {x}
                            </button>
                          );
                        })}
                      </div>
                    </Field>
                  </div>
                  <Field label="Wedding Segment" hint="optional — Haldi, Reception…">
                    <select data-testid="prod-segment" className={inputCls} value={form.segment} onChange={(e) => setForm({...form, segment: e.target.value})}>
                      <option value="">None</option>
                      {segments.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Gemstone" hint="optional">
                    <input data-testid="prod-gemstone" placeholder="e.g. Diamond, None" className={inputCls} value={form.gemstone} onChange={(e) => setForm({...form, gemstone: e.target.value})}/>
                  </Field>
                  <Field label="Collection" hint="optional — e.g. Festive 2026">
                    <input data-testid="prod-collection" placeholder="e.g. Bridal Heritage" className={inputCls} value={form.collection} onChange={(e) => setForm({...form, collection: e.target.value})}/>
                  </Field>
                </div>
              </div>

              {/* Description + media */}
              <div>
                <div className="eyebrow text-[10px] mb-3">Description & Media</div>
                <Field label="Description" hint="shown on the product page">
                  <textarea data-testid="prod-desc" placeholder="Describe the design, craftsmanship, occasion…" rows={3} className={`${inputCls} w-full`} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})}/>
                </Field>
                <div className="mt-4">
                  <div className="text-xs font-medium text-ink mb-2">
                    Product Images <span className="text-red-600">*</span>
                    <span className="text-ash font-normal"> — up to {maxImages} · minimum 2 · mark one as <b>Main</b> (card photo) and one as <b>Hover</b> (shown on mouse-over)</span>
                  </div>
                  <ImageUploader
                    value={images} onChange={handleImagesChange} kind="product" multiple maxFiles={maxImages}
                    markable primaryIndex={primaryIdx} hoverIndex={hoverIdx}
                    onSetPrimary={setPrimaryIdx} onSetHover={setHoverIdx}
                  />
                  {images.length > 0 && (primaryIdx == null || hoverIdx == null) && (
                    <p className="text-[11px] text-red-600 mt-2" data-testid="mark-images-hint">
                      Tap "Main" on your display image and "Hover" on the mouse-over image before saving.
                    </p>
                  )}
                </div>
                <div className="flex flex-wrap gap-4 mt-4 text-sm">
                  <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_bestseller} onChange={(e) => setForm({...form, is_bestseller: e.target.checked})}/> Bestseller</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_featured} onChange={(e) => setForm({...form, is_featured: e.target.checked})}/> Featured</label>
                  <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_new_arrival} onChange={(e) => setForm({...form, is_new_arrival: e.target.checked})}/> New arrival</label>
                </div>
              </div>

            </div>

            <div className="p-6 border-t border-line shrink-0">
              <button type="submit" className="btn-maroon w-full justify-center" data-testid="submit-product">
                {editingId ? "Save Changes" : "Create Product"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Stock modal (single-select) */}
      {addStock && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAddStock(null)}>
          <div className="bg-white border border-line w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()} data-testid="add-stock-modal">
            <div className="flex items-center justify-between mb-3">
              <div className="font-display text-xl text-maroon">Add stock</div>
              <button onClick={() => setAddStock(null)}><X size={18}/></button>
            </div>
            <div className="text-sm font-medium">{addStock.product.name}</div>
            <div className="text-[11px] text-ash mb-4">{addStock.product.barcode || addStock.product.category}</div>
            <label className="block text-sm mb-3">
              <span className="text-[10px] text-ash uppercase tracking-widest">Store</span>
              <select className={inputCls + " w-full mt-1"} value={addStock.store} onChange={(e) => setAddStock({ ...addStock, store: e.target.value })} data-testid="add-stock-store">
                {CANONICAL_STORES.map((s) => <option key={s} value={s}>{storeLabel(s)}</option>)}
              </select>
            </label>
            <label className="block text-sm mb-4">
              <span className="text-[10px] text-ash uppercase tracking-widest">Quantity to add</span>
              <input type="number" min={1} className={inputCls + " w-full mt-1"} value={addStock.qty} onChange={(e) => setAddStock({ ...addStock, qty: e.target.value })} data-testid="add-stock-qty"/>
            </label>
            <button onClick={submitAddStock} className="btn-maroon w-full justify-center" data-testid="add-stock-submit"><Plus size={14}/> Add to stock</button>
          </div>
        </div>
      )}
    </div>
  );
}